import { useState } from "react";
import { Mail, Linkedin, MapPin, FileText, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CV_URL = "/Piyus_Kumar_CV.pdf";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", enquiry_type: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.enquiry_type || !form.message) return;
    setSending(true);
    setError("");
    try {
      // Save to MongoDB
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to send");

      // EmailJS integration (only if configured)
      if (process.env.REACT_APP_EMAILJS_SERVICE_ID) {
        try {
          const emailjs = await import("@emailjs/browser");
          await emailjs.default.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
            { name: form.name, email: form.email, company: form.company || "Not provided", enquiry_type: form.enquiry_type, message: form.message },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );
        } catch {
          // EmailJS optional — MongoDB save is primary
        }
      }

      setSent(true);
      setForm({ name: "", email: "", company: "", enquiry_type: "", message: "" });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">Contact</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#111827] mt-3">
            Let's Work Together
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - info */}
          <div className="space-y-6">
            <p className="text-base text-[#6B7280] leading-relaxed">
              Whether you're looking to strengthen your security posture, validate a platform, train your team, or explore a senior hire — I'd love to hear from you.
            </p>

            {/* Advisory callout */}
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-4" data-testid="contact-advisory-callout">
              <p className="text-sm text-[#6B7280]">
                Need a dedicated security advisory engagement?{" "}
                <a href="https://shiftleftsecure.com" target="_blank" rel="noopener noreferrer" className="text-[#06B6D4] font-medium hover:underline" data-testid="contact-advisory-link">
                  Visit shiftleftsecure.com
                </a>{" "}
                for consulting services.
              </p>
            </div>

            <div className="space-y-4">
              <a href="mailto:piyush4855@gmail.com" className="flex items-center gap-3 text-sm text-[#111827] hover:text-[#06B6D4] transition-colors" data-testid="contact-email">
                <Mail className="w-5 h-5 text-[#06B6D4]" /> piyush4855@gmail.com
              </a>
              <a href="https://linkedin.com/in/kumarpiyus" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-[#111827] hover:text-[#06B6D4] transition-colors" data-testid="contact-linkedin">
                <Linkedin className="w-5 h-5 text-[#06B6D4]" /> linkedin.com/in/kumarpiyus
              </a>
              <div className="flex items-center gap-3 text-sm text-[#111827]" data-testid="contact-location">
                <MapPin className="w-5 h-5 text-[#06B6D4]" /> Mumbai, India — Remote engagements welcome
              </div>
            </div>

            <div className="pt-2">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">Available for</p>
              <div className="flex flex-wrap gap-2">
                {["Consulting", "Advisory", "Training", "Full-time Opportunities"].map((t) => (
                  <span key={t} className="px-3 py-1 text-xs font-medium text-[#111827] bg-[#F9FAFB] border border-[#E5E7EB] rounded-full">{t}</span>
                ))}
              </div>
            </div>

            <a
              href={CV_URL}
              download
              data-testid="contact-cv-btn"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#0F172A] text-white text-sm font-medium transition-all hover:bg-[#111827] hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] mt-4"
            >
              <FileText className="mr-2 w-4 h-4" /> Download CV
            </a>
          </div>

          {/* Right - form */}
          <div>
            {sent ? (
              <div className="bg-[#06B6D4]/5 border border-[#06B6D4]/20 rounded-2xl p-8 text-center" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center mx-auto mb-4">
                  <Send className="w-5 h-5 text-[#06B6D4]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-[#111827] mb-2">Message Sent!</h3>
                <p className="text-sm text-[#6B7280]">Thank you for reaching out. I'll get back to you shortly.</p>
                <button onClick={() => setSent(false)} className="mt-4 text-sm text-[#06B6D4] font-medium hover:underline" data-testid="send-another-btn">Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="text-sm font-medium text-[#111827] mb-1.5 block">Name *</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    className="bg-white border-[#E5E7EB] focus:ring-[#06B6D4]/50 focus:border-[#06B6D4]"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111827] mb-1.5 block">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com"
                    required
                    className="bg-white border-[#E5E7EB] focus:ring-[#06B6D4]/50 focus:border-[#06B6D4]"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111827] mb-1.5 block">Company</label>
                  <Input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Your company name"
                    className="bg-white border-[#E5E7EB] focus:ring-[#06B6D4]/50 focus:border-[#06B6D4]"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111827] mb-1.5 block">Enquiry Type *</label>
                  <Select value={form.enquiry_type} onValueChange={(v) => setForm({ ...form, enquiry_type: v })}>
                    <SelectTrigger className="bg-white border-[#E5E7EB]" data-testid="contact-enquiry-select">
                      <SelectValue placeholder="Select enquiry type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Consulting / Advisory">Consulting / Advisory</SelectItem>
                      <SelectItem value="Job Opportunity">Job Opportunity</SelectItem>
                      <SelectItem value="Training / Workshop">Training / Workshop</SelectItem>
                      <SelectItem value="Speaking Invitation">Speaking Invitation</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111827] mb-1.5 block">Message *</label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell me about your project or enquiry..."
                    rows={5}
                    required
                    className="bg-white border-[#E5E7EB] focus:ring-[#06B6D4]/50 focus:border-[#06B6D4]"
                    data-testid="contact-message-input"
                  />
                </div>
                {error && <p className="text-sm text-red-500" data-testid="contact-error">{error}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#0F172A] text-white font-medium transition-all hover:bg-[#111827] hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
